import {
  Button,
  Card,
  DatePicker,
  EmptyState,
  FormField,
  Icon,
  ListToolbar,
  Select,
  Text,
  type FilterChip,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PageActions } from '../../../app/PageActions';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useDatePresets, useFacetTexts, useRangeLabel } from '../../../lib/facets';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useResetUrlState, useSetUrlStates, useUrlState } from '../../../lib/useUrlState';
import { useSession } from '../../identity/session';
import { useReferrers, useSales, type SaleStatus } from '../api';
import { SaleFormDialog } from '../components/SaleFormDialog';
import { SalesTable, useSaleColumns } from '../components/SalesTable';
import styles from './SalesPages.module.css';

const STATUSES = ['draft', 'ordered', 'delivered', 'cancelled'] as const;
const PAYMENTS = ['offen', 'bezahlt'] as const;
const FILTER_KEYS = ['status', 'zahlung', 'von', 'bis', 'vermittelt'] as const;

/** All sales, newest first, filterable by period, status and payment (SAL-007). */
export function SalesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useSession();
  const columnSort = useColumnSort();
  const [status, setStatus] = useUrlState<SaleStatus | ''>('status', '', STATUSES);
  const [payment, setPayment] = useUrlState<(typeof PAYMENTS)[number] | ''>(
    'zahlung',
    '',
    PAYMENTS,
  );
  const [from, setFrom] = useUrlState('von', '');
  const [to, setTo] = useUrlState('bis', '');
  // "Vermittelt von" (#26), a member id.
  const [referrer, setReferrer] = useUrlState('vermittelt', '');
  const referrers = useReferrers();
  const resetFilters = useResetUrlState(FILTER_KEYS);
  const setUrlStates = useSetUrlStates();
  const facetTexts = useFacetTexts(resetFilters);
  const presets = useDatePresets();
  const range = useRangeLabel(from, to);
  const [recording, setRecording] = useState(false);
  const sales = useSales({
    status: status || undefined,
    paid: payment === '' ? undefined : payment === 'bezahlt',
    from: from || undefined,
    to: to || undefined,
    referred_by_id: referrer || undefined,
    // Paged list: the server sorts, so "Weitere laden" continues in order (#188).
    sort: columnSort.value || undefined,
  });
  const rows = sales.data?.pages.flatMap((p) => p.items) ?? [];
  const sortMenu = useSortMenu(useSaleColumns(), rows, columnSort);
  const write = can('sales.write');
  const filtered = status !== '' || payment !== '' || from !== '' || to !== '' || referrer !== '';
  const empty = !sales.isPending && rows.length === 0 && !filtered;
  const record = () => setRecording(true);

  const chips: FilterChip[] = [
    ...(status
      ? [
          {
            key: 'status',
            label: t('sales.filters.chipStatus', { status: t(`sales.status.${status}`) }),
            onRemove: () => setStatus(''),
          },
        ]
      : []),
    ...(payment
      ? [
          {
            key: 'payment',
            label: t('sales.filters.chipPayment', {
              payment: t(payment === 'offen' ? 'sales.payment.open' : 'sales.payment.paid'),
            }),
            onRemove: () => setPayment(''),
          },
        ]
      : []),
    ...(from
      ? [
          {
            key: 'from',
            label: t('sales.filters.chipFrom', { date: formatDate(from) }),
            onRemove: () => setFrom(''),
          },
        ]
      : []),
    ...(to
      ? [
          {
            key: 'to',
            label: t('sales.filters.chipTo', { date: formatDate(to) }),
            onRemove: () => setTo(''),
          },
        ]
      : []),
    ...(referrer
      ? [
          {
            key: 'referrer',
            label: t('sales.referrer.chip', {
              name: referrers.data?.find((m) => m.id === referrer)?.name ?? '…',
            }),
            onRemove: () => setReferrer(''),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.page}>
      {write && (
        <PageActions>
          <Button iconStart={<Icon name="plus" size="sm" />} onClick={record}>
            {t('sales.quick.sale')}
          </Button>
        </PageActions>
      )}

      {!empty && (
        <ListToolbar
          facets={{
            ...facetTexts,
            items: [
              {
                type: 'select',
                key: 'status',
                label: t('sales.filters.status'),
                value: status,
                options: STATUSES.map((s) => ({ value: s, label: t(`sales.status.${s}`) })),
                onChange: (v) => setStatus(v as SaleStatus | ''),
              },
              {
                type: 'select',
                key: 'payment',
                label: t('sales.filters.payment'),
                value: payment,
                options: [
                  { value: 'offen', label: t('sales.payment.open') },
                  { value: 'bezahlt', label: t('sales.payment.paid') },
                ],
                onChange: (v) => setPayment(v as (typeof PAYMENTS)[number] | ''),
              },
              {
                type: 'select',
                key: 'referrer',
                label: t('sales.referrer.filter'),
                value: referrer,
                options: (referrers.data ?? []).map((m) => ({ value: m.id, label: m.name })),
                onChange: setReferrer,
              },
              {
                type: 'dates',
                key: 'period',
                label: t('sales.filters.period'),
                from,
                to,
                fromLabel: t('sales.filters.from'),
                toLabel: t('sales.filters.to'),
                valueLabel: range,
                presets,
                onChange: (r) => setUrlStates({ von: r.from, bis: r.to }),
              },
            ],
          }}
          filters={{
            label: t('common.filter'),
            activeCount: chips.length,
            content: (
              <>
                <FormField label={t('sales.filters.status')}>
                  {(p) => (
                    <Select
                      {...p}
                      value={status}
                      onChange={(e) => setStatus(e.target.value as SaleStatus | '')}
                      options={[
                        { value: '', label: t('sales.filters.allStatuses') },
                        ...STATUSES.map((s) => ({ value: s, label: t(`sales.status.${s}`) })),
                      ]}
                    />
                  )}
                </FormField>
                <FormField label={t('sales.filters.payment')}>
                  {(p) => (
                    <Select
                      {...p}
                      value={payment}
                      onChange={(e) => setPayment(e.target.value as (typeof PAYMENTS)[number] | '')}
                      options={[
                        { value: '', label: t('sales.filters.allPayments') },
                        { value: 'offen', label: t('sales.payment.open') },
                        { value: 'bezahlt', label: t('sales.payment.paid') },
                      ]}
                    />
                  )}
                </FormField>
                <FormField label={t('sales.referrer.filter')}>
                  {(p) => (
                    <Select
                      {...p}
                      value={referrer}
                      onChange={(e) => setReferrer(e.target.value)}
                      options={[
                        { value: '', label: t('sales.referrer.all') },
                        ...(referrers.data ?? []).map((m) => ({ value: m.id, label: m.name })),
                      ]}
                    />
                  )}
                </FormField>
                <FormField label={t('sales.filters.from')}>
                  {(p) => <DatePicker {...p} value={from} onChange={setFrom} />}
                </FormField>
                <FormField label={t('sales.filters.to')}>
                  {(p) => <DatePicker {...p} value={to} onChange={setTo} />}
                </FormField>
              </>
            ),
          }}
          chips={{
            chips,
            removeLabel: (label) => t('common.removeFilter', { label }),
            resetLabel: t('common.resetAll'),
            onReset: resetFilters,
          }}
          sort={sortMenu}
        />
      )}

      <Card padding="none">
        {sales.error ? (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-4)' }}>
            {errorMessage(sales.error)}
          </Text>
        ) : (
          <SalesTable
            {...columnSort}
            presorted
            caption={t('sales.list.caption')}
            rows={rows}
            loading={sales.isPending}
            // Count and sum only when filtered, e.g. open orders (docs/ux-richtlinien.md §6.1).
            showTotal={filtered && !sales.hasNextPage}
            empty={
              filtered ? (
                <EmptyState
                  title={t('common.noResults')}
                  actions={
                    <Button variant="secondary" onClick={resetFilters}>
                      {t('common.resetFilters')}
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  title={t('sales.list.emptyTitle')}
                  actions={write && <Button onClick={record}>{t('sales.quick.sale')}</Button>}
                />
              )
            }
          />
        )}
        {sales.hasNextPage && (
          <div className={styles.more}>
            <Button
              variant="secondary"
              loading={sales.isFetchingNextPage}
              onClick={() => void sales.fetchNextPage()}
            >
              {t('sales.list.more')}
            </Button>
          </div>
        )}
      </Card>

      {recording && (
        <SaleFormDialog
          open
          onClose={() => setRecording(false)}
          onSaved={(s) => void navigate(`/verkauf/${s.id}`)}
        />
      )}
    </div>
  );
}
